import React from "react";

interface StatusToggleProps {
  /** Current active status */
  isActive: boolean;
  /** Callback function when toggle is clicked */
  onChange: (isActive: boolean) => void;
  /** Optional custom fontSize (default: "1.5rem") */
  fontSize?: string;
  /** Optional custom className */
  className?: string;
  /** Optional custom style */
  style?: React.CSSProperties;
  /** Optional disabled state */
  disabled?: boolean;
}

/**
 * Reusable Status Toggle Component
 * 
 * Displays a toggle icon that matches the Category status chip style.
 * Can be used in table cells, forms, and anywhere status toggling is needed.
 * 
 * @example
 * // In a table cell
 * <StatusToggle
 *   isActive={item.is_active}
 *   onChange={(newStatus) => handleStatusToggle(item, newStatus)}
 * />
 * 
 * @example
 * // In a form section
 * <StatusToggle
 *   isActive={enabled}
 *   onChange={(checked) => handleToggle(checked)}
 *   style={{ marginLeft: "10px" }}
 * />
 */
const StatusToggle: React.FC<StatusToggleProps> = ({
  isActive,
  onChange,
  fontSize = "1.5rem",
  className = "",
  style = {},
  disabled = false,
}) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!isActive);
    }
  };

  return (
    <div
      className={className}
      style={{
        fontSize,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        display: "inline-block",
        ...style,
      }}
      onClick={handleClick}
      title={disabled ? "Disabled" : isActive ? "Click to deactivate" : "Click to activate"}
    >
      <i
        className={`bi ${
          isActive
            ? "bi-toggle-on text-primary"
            : "bi-toggle-off text-danger"
        }`}
      />
    </div>
  );
};

export default StatusToggle;

